import React, { useEffect, useRef, useState } from "react";
import { IListFilterItem } from "source/models/common/listFilterItem";
import { DrawerContent } from "../../framework/Drawer/DrawerContent";
import { FilterButtons } from "../../framework/Filters/FilterButtons";
import { FilterSearchField } from "../../framework/Filters/FilterSearchField";
import { GridRow } from "../../framework/Grid/GridRow";
import { PageFilterConditionDrawer } from "../../framework/Page/PageFilterConditionDrawer";
import { Grid } from "@mui/material";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { Translations } from "ui/Scripts/source/models/translations";
import { CalendarTaskCategoryNotSetId, CalendarTaskStateFilterAll, ICalendarTaskPageFilterLists, ICalendarTaskPageFilterState } from "./CalendarTaskPageUtils";
import { SaveDataObj } from "source/framework/saveData";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { AppUtils } from "source/framework/appUtils";

interface ICalendarTaskPageFilterDrawerProps extends ICalendarTaskPageFilterLists, ICalendarTaskPageFilterState {
  open: boolean;
  filter?: IListFilterItem;
  canRemoveSharedFilters: boolean;
  onSaveFilter: (saveAs: boolean, onGetGeneralFilter: () => any) => void;
  onRemoveFilter: (filterId: string) => void;
  onOk: (closeDialog: boolean, filterState: ICalendarTaskPageFilterState) => any;
  onCancel: () => void;

}

export const calendarTaskFilterIsSet = (filterLists: ICalendarTaskPageFilterLists, filterState: ICalendarTaskPageFilterState): boolean => {
    return !(Base.equalStrArrays(filterState.selectedStateIds, [filterLists.states[0].id]) &&
        Base.equalStrArrays(filterState.selectedCategoryIds, [filterLists.categories[0].id]) &&
        Base.equalStrArrays(filterState.selectedHandlerUserGroupCodes, [filterLists.handlers[0].id]) &&
        Base.equalStrArrays(filterState.selectedCreateUserCodes, [filterLists.creators[0].id]) &&
        filterState.selectedPrivacy === filterLists.privacy[0].id);
};

export const CalendarTaskPageFilterDrawer = (props: ICalendarTaskPageFilterDrawerProps) => {
    const getHashFromState = (state: ICalendarTaskPageFilterState): string => {
        const result = new SaveDataObj();
        result.append("selectedStateIds", state.selectedStateIds);
        result.append("selectedCategoryIds", state.selectedCategoryIds);
        result.append("selectedHandlerUserGroupCodes", state.selectedHandlerUserGroupCodes);
        result.append("selectedCreateUserCodes", state.selectedCreateUserCodes);
        result.append("selectedPrivacy", state.selectedPrivacy);
        return result.hash;
    };

    const getStateFromProps = (props: ICalendarTaskPageFilterDrawerProps): ICalendarTaskPageFilterState => {
        return {
            selectedStateIds: props.selectedStateIds,
            selectedCategoryIds: props.selectedCategoryIds,
            selectedHandlerUserGroupCodes: props.selectedHandlerUserGroupCodes,
            selectedCreateUserCodes: props.selectedCreateUserCodes,
            selectedPrivacy: props.selectedPrivacy
        };
    };

    const orgStateHash = useRef(getHashFromState(props));
    const [state, setState] = useState<ICalendarTaskPageFilterState>(getStateFromProps(props));

    useEffect(() => {
        if (!props.open) return;
        const newState = getStateFromProps(props);
        setState((old) => ({
            ...old,
            ...newState
        }));
        orgStateHash.current = getHashFromState(newState);
    }, [props.open]);

    const getSaveFilterState = (): ICalendarTaskPageFilterState => {
        return {
            selectedCategoryIds: state.selectedCategoryIds,
            selectedStateIds: state.selectedStateIds,
            selectedHandlerUserGroupCodes: state.selectedHandlerUserGroupCodes,
            selectedCreateUserCodes: state.selectedCreateUserCodes,
            selectedPrivacy: state.selectedPrivacy
        };
    };

    const handleSaveFilter = async (saveAs: boolean) => {
        const filterState = getSaveFilterState();
        if (!saveAs) {
            props.onOk(false, filterState);
            await props.onSaveFilter(false, null);
        } else {
            await props.onSaveFilter(true, () => {
                return props.onOk(false, filterState);
            });
        }
    };

    const handleClear = () => {
        setState((old) => ({
            ...old,
            selectedStateIds: [props.states[0].id],
            selectedCategoryIds: [props.categories[0].id],
            selectedHandlerUserGroupCodes: [props.handlers[0].id],
            selectedCreateUserCodes: [props.creators[0].id],
            selectedPrivacy: props.privacy[0].id
        }));
    };

    const handleSave = async () => {
        props.onOk(true, getSaveFilterState());
    };

    const handleCancel = async (checkChanges: boolean) => {
        AppUtils.cancelEditDialog(checkChanges, getHashFromState(state), orgStateHash.current, handleSave, props.onCancel, Translations.YouHaveUnapprovedChangesDoYouWantApproveChanges);
    };

    return (
        <PageFilterConditionDrawer
            open={props.open}
            onClose={() => handleCancel(false)}
        >
            <DrawerContent
                content={
                    <>
                        {!!props.onSaveFilter && !!props.onRemoveFilter && (
                            <FilterSearchField
                                filter={props.filter}
                                canRemoveSharedFilters={props.canRemoveSharedFilters}
                                onSaveFilter={handleSaveFilter}
                                onRemoveFilter={props.onRemoveFilter}
                            />
                        )}
                        <GridRow>
                            <Grid item sm={12} md={6}>
                                <FormField title={Translations.State}>
                                    <CheckboxDropdown
                                        required={true}
                                        selectedIds={state.selectedStateIds}
                                        toggleAll={true}
                                        toggleAllId={CalendarTaskStateFilterAll.toString(10)}
                                        values={props.states}
                                        onChange={(selectedStateIds: string[]) =>
                                            setState((old) => ({ ...old, selectedStateIds }))
                                        }
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={12} md={6}>
                                <FormField title={Translations.CalendarTaskCategory}>
                                    <CheckboxDropdown
                                        required={true}
                                        selectedIds={state.selectedCategoryIds}
                                        toggleAll={true}
                                        toggleAllId={Base.emptyGuid}
                                        toggleSingleSelectIds={[CalendarTaskCategoryNotSetId]}
                                        values={props.categories}
                                        onChange={(selectedCategoryIds: string[]) =>
                                            setState((old) => ({ ...old, selectedCategoryIds }))
                                        }
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12} md={6}>
                                <FormField title={Translations.CalendarTaskHandlerFilter}>
                                    <CheckboxDropdown
                                        required={true}
                                        selectedIds={state.selectedHandlerUserGroupCodes}
                                        toggleAll={true}
                                        values={props.handlers}
                                        onChange={(selectedHandlerUserGroupCodes: string[]) => setState((old) => ({ ...old, selectedHandlerUserGroupCodes }))} />
                                </FormField>
                            </Grid>
                            <Grid item sm={12} md={6}>
                                <FormField title={Translations.Private}>
                                    <IdTitleDropdown
                                        selectedId={state.selectedPrivacy}
                                        items={props.privacy}
                                        onChange={(selectedPrivacy: string) => setState((old) => ({ ...old, selectedPrivacy }))}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12} md={6}>
                                <FormField title={Translations.TaskCreated}>
                                    <CheckboxDropdown
                                        required={true}
                                        selectedIds={state.selectedCreateUserCodes}
                                        toggleAll={true}
                                        values={props.creators}
                                        onChange={(selectedCreateUserCodes: string[]) => setState((old) => ({ ...old, selectedCreateUserCodes }))} />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </>
                }
                actions={
                    <FilterButtons
                        onSave={handleSave}
                        onCancel={() => handleCancel(false)}
                        onClear={handleClear}
                    />
                }
            />
        </PageFilterConditionDrawer>
    );
};
